import React, { Fragment, useState, useEffect } from 'react';
import {
  Text,
  Group,
  Button,
  Menu,
  Modal,
  Select,
  Stack,
  Divider,
  Grid,
  Badge,
  Popover,
  TextInput,
  CloseButton,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { EditableText } from '@ayocore/platform/client/components/EditableText';
import { ViewPanel, InfoRow } from '@ayocore/platform/client/components/EntityViewLayout';
import { DeleteIconButton, AddIconButton } from '@ayocore/platform/client/components/elements';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { Checkbox } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import { contactPersonRepository } from '@ayocore/erp/crm/client/rpc';
import { contactChannelRepository } from '@ayocore/erp/crm/client/rpc';
import { EditChannelForm } from '@ayocore/erp/crm/client/components/ChannelManager/ChannelManager';
import contactPersonSchemaDefinition from '@ayocore/erp/crm/schemas/contact-person.schema';
import type { ContactPerson } from '@ayocore/erp/crm/schemas/contact-person.schema';
import contactChannelSchemaDefinition from '@ayocore/erp/crm/schemas/contact-channel.schema';
import type {
  ContactChannelCreate,
  ContactChannel,
} from '@ayocore/erp/crm/schemas/contact-channel.schema';
import { StringField } from '@ayocore/platform/common/helpers/schema-types';
import { formatPhone } from '@ayocore/platform/client/components/EditableText/formatters/formatPhone';

/** Типы каналов для быстрого добавления внутри блока контактного лица */
const QUICK_ADD_CONTACT_CHANNEL_TYPES = [
  { value: 'phone', label: 'Телефон' },
  { value: 'email', label: 'Почта' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'website', label: 'Сайт' },
  { value: 'instagram', label: 'Instagram' },
];

const CHANNEL_LABEL_MAP: Record<string, string> = {
  phone: 'Телефон',
  email: 'Почта',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  website: 'Сайт',
  instagram: 'Instagram',
  viber: 'Viber',
  skype: 'Skype',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  twitter: 'Twitter',
  youtube: 'YouTube',
};

interface ContactPersonManagerProps {
  entityId: number;
  entityType: 'crm::PartnerRepository' | 'crm::LeadRepository';
  contactPersons: (ContactPerson & {
    is_primary?: boolean;
    is_active?: boolean;
    role?: string;
    contact_channels?: ContactChannel[];
  })[];
  onContactPersonsChanged: () => void;
  /** Показать всех контактных лиц, а не только основных */
  showAll?: boolean;
  /** Показать поле поиска */
  showSearch?: boolean;
  /**
   * Можно ли заводить новые контактные лица и каналы связи у них.
   * Дефолт true — экраны без своей политики работают как раньше.
   */
  canAdd?: boolean;
  /**
   * Можно ли править карточку конкретного контактного лица (имя, должность,
   * заметки, признаки «основной»/ЛПР/активен, удаление). Право считает сервер
   * по каждой записи. Дефолт — всё можно.
   */
  canEditContact?: (contact: ContactPerson) => boolean;
  /** То же для конкретного канала связи контактного лица. */
  canEditChannel?: (channel: ContactChannel) => boolean;
}

type CreateContactPersonData = Parameters<typeof contactPersonRepository.createExtended>[0];
type UpdateContactPersonData = Parameters<typeof contactPersonRepository.update>[1];
type AddContactChannelData = ContactChannelCreate;
type UpdateContactChannelData = Parameters<typeof contactChannelRepository.update>[1];

export function ContactPersonManager({
  entityId,
  entityType,
  contactPersons,
  onContactPersonsChanged,
  showAll = false,
  showSearch = false,
  canAdd = true,
  canEditContact = () => true,
  canEditChannel = () => true,
}: ContactPersonManagerProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Инлайн-модалки (не через useModal): z-index из общего аллокатора, иначе при
  // открытии менеджера внутри дравера (Lead/Partner view) модалки окажутся под ним.
  const addModalZIndex = useSurfaceZIndex(showAddModal);
  const editModalZIndex = useSurfaceZIndex(showEditModal);
  const [editingContact, setEditingContact] = useState<ContactPerson | null>(null);
  const [editingIsPrimary, setEditingIsPrimary] = useState<boolean>(false);
  const [channelsByContactId, setChannelsByContactId] = useState<Record<number, ContactChannel[]>>(
    {}
  );
  const [showAddChannelsModal, setShowAddChannelsModal] = useState(false);
  const addChannelsModalZIndex = useSurfaceZIndex(showAddChannelsModal);
  const [selectedContactForChannels, setSelectedContactForChannels] =
    useState<ContactPerson | null>(null);
  const [showEditChannelModal, setShowEditChannelModal] = useState(false);
  const editChannelModalZIndex = useSurfaceZIndex(showEditChannelModal);
  const [editingChannel, setEditingChannel] = useState<ContactChannel | null>(null);
  const [editingChannelContactId, setEditingChannelContactId] = useState<number | null>(null);
  const [addingDescriptionForKey, setAddingDescriptionForKey] = useState<string | null>(null);
  const [quickAddMenuOpenForId, setQuickAddMenuOpenForId] = useState<number | null>(null);
  const [quickAddingFor, setQuickAddingFor] = useState<{
    contactId: number;
    contactUuid: string;
    type: string;
  } | null>(null);
  const [editingBirthdayForId, setEditingBirthdayForId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<typeof contactPersons | null>(null);

  const hoverStyles = `
    .contact-item:hover .delete-icon { opacity: 1 !important; }
    .contact-item:hover .lpr-hint { opacity: 1 !important; }
    .contact-item:hover .primary-hint { opacity: 1 !important; }
.contact-item:hover .birthday-hint { opacity: 1 !important; }
    .channel-value-group:hover .add-description-hint { opacity: 1 !important; }
    .channel-value-group:hover .delete-channel-icon { opacity: 1 !important; }
  `;

  /**
   * Создаёт контакт и привязывает к сущности с флагом is_primary (если отмечен).
   */
  const handleAddContact = async (values: CreateContactPersonData, isPrimary: boolean) => {
    try {
      // Call server stub to log extended fields and create a basic contact
      const contactPerson = await contactPersonRepository.createExtended(values);
      logger.info('✅ Contact person (extended stub) created:', contactPerson);

      // TODO: refactor: move assignment into server flow if needed
      // Assign to entity based on type
      if (entityType === 'crm::PartnerRepository') {
        await contactPersonRepository.assignToPartner(
          Number(contactPerson.id),
          Number(entityId),
          !!isPrimary
        );
        logger.info('✅ Contact assigned to partner');
      } else if (entityType === 'crm::LeadRepository') {
        await contactPersonRepository.assignToLead(
          Number(contactPerson.id),
          Number(entityId),
          !!isPrimary
        );
        logger.info('✅ Contact assigned to lead');
      }

      logger.info('🔄 Calling onContactPersonsChanged...');
      onContactPersonsChanged();
      logger.info('✅ Modal closing...');
      setShowAddModal(false);
      logger.info('✅ All done!');
    } catch (error) {
      logger.error('❌ Error adding contact:', error);
      throw error; // Re-throw to let SchemaForm handle it
    }
  };

  /**
   * Сохраняет изменения контактного лица и флаг связи is_primary для текущей сущности (partner/lead).
   */
  const handleEditContact = async (values: UpdateContactPersonData) => {
    if (!editingContact) return;

    try {
      await contactPersonRepository.update(editingContact.id, values);
      // Обновляем флаг связи "Основной" для текущей сущности
      if (entityType === 'crm::PartnerRepository') {
        await contactPersonRepository.assignToPartner(
          editingContact.id,
          entityId,
          !!editingIsPrimary
        );
      } else if (entityType === 'crm::LeadRepository') {
        await contactPersonRepository.assignToLead(editingContact.id, entityId, !!editingIsPrimary);
      }
      onContactPersonsChanged();
      setShowEditModal(false);
      setEditingContact(null);
      setEditingIsPrimary(false);
    } catch (error) {
      logger.error('Error updating contact:', error);
    }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!confirm('Удалить это контактное лицо?')) return;

    // TODO: долбоебизм делать if тут
    try {
      if (entityType === 'crm::PartnerRepository') {
        await contactPersonRepository.removeFromPartner(contactId, entityId);
      } else if (entityType === 'crm::LeadRepository') {
        await contactPersonRepository.removeFromLead(contactId, entityId);
      }
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error deleting contact:', error);
    }
  };

  /**
   * Создаёт контактное лицо с минимальными данными (только имя) и привязывает к сущности как основное.
   */
  const handleInlineCreateContact = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const contactPerson = await contactPersonRepository.createExtended({ name: trimmed });
      if (entityType === 'crm::PartnerRepository') {
        await contactPersonRepository.assignToPartner(
          Number(contactPerson.id),
          Number(entityId),
          true
        );
      } else if (entityType === 'crm::LeadRepository') {
        await contactPersonRepository.assignToLead(
          Number(contactPerson.id),
          Number(entityId),
          true
        );
      }
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error creating contact person inline:', error);
    }
  };

  /**
   * Создаёт канал связи для конкретного контактного лица и обновляет локальный кэш каналов.
   */
  const handleInlineCreateContactChannel = async (
    contactUuid: string,
    contactId: number,
    contactType: string,
    value: string
  ) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    try {
      await contactChannelRepository.addChannelToEntity(contactUuid, {
        contact_type: contactType,
        contact_value: trimmed,
        is_verified: false,
        is_active: true,
      } as ContactChannelCreate);
      const fresh = await contactPersonRepository.getContactPersonContacts(contactId);
      setChannelsByContactId(prev => ({ ...prev, [contactId]: fresh || [] }));
    } catch (error) {
      logger.error('Error creating contact channel inline:', error);
    }
  };

  const handleAddContactChannels = async (values: AddContactChannelData) => {
    if (!selectedContactForChannels) return;

    try {
      const contactId = selectedContactForChannels.id;
      await contactChannelRepository.addChannelToEntity(selectedContactForChannels.uuid, values);
      setShowAddChannelsModal(false);
      setSelectedContactForChannels(null);
      // Invalidate cache for this contact to force fresh reload of channels
      setChannelsByContactId(prev => {
        const next = { ...prev } as typeof prev;
        delete next[contactId];
        return next;
      });
      // Immediately load fresh channels for the updated contact
      try {
        const fresh = await contactPersonRepository.getContactPersonContacts(contactId);
        setChannelsByContactId(prev => ({ ...prev, [contactId]: fresh || [] }));
      } catch (e) {
        logger.error(e);
      }
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error adding contact channel:', error);
      throw error;
    }
  };
  const filteredContacts = React.useMemo(
    () =>
      showAll ? contactPersons || [] : (contactPersons || []).filter(c => c.is_active !== false),
    [contactPersons, showAll]
  );

  /** Когда есть результаты поиска — показываем их, иначе обычный список */
  const primaryContacts = searchResults ?? filteredContacts;

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const results = await contactPersonRepository
        .filterByEntity(entityType, entityId)
        .search(query.trim())
        .withContactChannelList()
        .fetch();
      setSearchResults((results ?? []) as typeof contactPersons);
    } catch (e) {
      logger.error('[ContactPersonManager] Search failed', e);
    }
  };

  const clearSearch = () => {
    setSearchValue('');
    setSearchResults(null);
  };

  const VALUE_MIN_WIDTH = 180;

  const formatBirthday = (date: string | null | undefined) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  };

  const handleInlineSaveChannelDescription = async (
    channelId: number,
    contactId: number,
    newValue: string
  ) => {
    try {
      await contactChannelRepository.update(channelId, { description: newValue.trim() || null });
      const fresh = await contactPersonRepository.getContactPersonContacts(contactId);
      setChannelsByContactId(prev => ({ ...prev, [contactId]: fresh || [] }));
    } catch (error) {
      const fresh = await contactPersonRepository.getContactPersonContacts(contactId);
      setChannelsByContactId(prev => ({ ...prev, [contactId]: fresh || [] }));
      logger.error('Error updating channel description:', error);
    }
  };

  const handleInlineSaveChannel = async (
    channelId: number,
    contactId: number,
    newValue: string
  ) => {
    try {
      await contactChannelRepository.update(channelId, { contact_value: newValue.trim() });
      const fresh = await contactPersonRepository.getContactPersonContacts(contactId);
      setChannelsByContactId(prev => ({ ...prev, [contactId]: fresh || [] }));
    } catch (error) {
      const fresh = await contactPersonRepository.getContactPersonContacts(contactId);
      setChannelsByContactId(prev => ({ ...prev, [contactId]: fresh || [] }));
      logger.error('Error updating channel:', error);
    }
  };

  const handleDeleteChannel = async (contactUuid: string, contactId: number, channelId: number) => {
    if (!confirm('Удалить этот контакт?')) return;
    try {
      await contactChannelRepository.removeChannelFromEntity(contactUuid, channelId);
      const fresh = await contactPersonRepository.getContactPersonContacts(contactId);
      setChannelsByContactId(prev => ({ ...prev, [contactId]: fresh || [] }));
    } catch (error) {
      logger.error('Error deleting channel:', error);
    }
  };

  const handleToggleDecisionMaker = async (contactId: number, current: boolean) => {
    try {
      await contactPersonRepository.update(contactId, { is_decision_maker: !current });
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error toggling decision maker:', error);
    }
  };

  const handleTogglePrimary = async (contactId: number, current: boolean) => {
    try {
      if (entityType === 'crm::PartnerRepository') {
        await contactPersonRepository.assignToPartner(contactId, entityId, !current);
      } else if (entityType === 'crm::LeadRepository') {
        await contactPersonRepository.assignToLead(contactId, entityId, !current);
      }
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error toggling primary:', error);
    }
  };

  const handleToggleActive = async (contactId: number) => {
    try {
      await contactPersonRepository.togglePartnerActive(contactId, entityId);
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error toggling active status:', error);
    }
  };

  const handleInlineSaveBirthday = async (contactId: number, date: Date | string | null) => {
    if (!date) {
      await contactPersonRepository.update(contactId, { birthday: null });
      onContactPersonsChanged();
      return;
    }
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return;
    try {
      await contactPersonRepository.update(contactId, { birthday: d.toISOString() });
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error updating birthday:', error);
    }
  };

  const handleInlineSaveContactNotes = async (contactId: number, newValue: string) => {
    const trimmed = newValue.trim();
    try {
      await contactPersonRepository.update(contactId, { notes: trimmed || null });
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error updating contact notes:', error);
    }
  };

  const handleInlineSavePosition = async (contactId: number, newValue: string) => {
    const trimmed = newValue.trim();
    try {
      await contactPersonRepository.update(contactId, { position: trimmed || null });
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error updating contact position:', error);
    }
  };

  const handleInlineSaveContactName = async (contactId: number, newValue: string) => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    try {
      await contactPersonRepository.update(contactId, { name: trimmed });
      onContactPersonsChanged();
    } catch (error) {
      logger.error('Error updating contact name:', error);
    }
  };

  const renderContactValue = (
    channel: ContactChannel,
    contactId: number,
    contactUuid: string,
    label: string
  ) => (
    <InfoRow>
      <InfoRow.TitleCol>{label}</InfoRow.TitleCol>
      <InfoRow.ValueCol>
        <Group
          gap='xs'
          align='flex-start'
          wrap='nowrap'
          className='channel-value-group'
          style={{ minWidth: 0, flex: 1 }}
        >
          <EditableText
            value={channel.contact_value}
            size='sm'
            canEdit={canEditChannel(channel)}
            allowEmptySave
            formatValue={channel.contact_type === 'phone' ? formatPhone : undefined}
            onSave={v => handleInlineSaveChannel(channel.id, contactId, v)}
            style={{
              minWidth: VALUE_MIN_WIDTH,
              flexShrink: 0,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          />
          {(() => {
            const desc = channel.description ?? '';
            const key = `${contactId}-${channel.id}`;
            const isAdding = addingDescriptionForKey === key;
            if (desc) {
              return (
                <EditableText
                  value={desc}
                  size='sm'
                  canEdit={canEditChannel(channel)}
                  allowEmptySave
                  onSave={v => handleInlineSaveChannelDescription(channel.id, contactId, v)}
                  style={{ flex: 1, minWidth: 0, color: 'var(--mantine-color-dimmed)' }}
                />
              );
            }
            if (isAdding) {
              return (
                <EditableText
                  value=''
                  size='sm'
                  canEdit
                  placeholder='Описание...'
                  defaultEditing
                  onSave={v => {
                    setAddingDescriptionForKey(null);
                    void handleInlineSaveChannelDescription(channel.id, contactId, v);
                  }}
                  onCancel={() => setAddingDescriptionForKey(null)}
                  style={{ flex: 1, minWidth: 0, color: 'var(--mantine-color-dimmed)' }}
                />
              );
            }
            // Подсказка «добавить описание» — affordance правки, без прав не нужна
            if (!canEditChannel(channel)) return null;
            return (
              <Text
                component='span'
                size='sm'
                className='add-description-hint'
                style={{
                  flex: 1,
                  minWidth: 0,
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  color: 'var(--mantine-color-gray-5)',
                  fontStyle: 'italic',
                  cursor: 'pointer',
                }}
                onClick={() => setAddingDescriptionForKey(key)}
              >
                Добавить описание
              </Text>
            );
          })()}
          {canEditChannel(channel) && (
            <div
              className='delete-channel-icon'
              style={{ opacity: 0, transition: 'opacity 0.2s ease', flexShrink: 0 }}
            >
              <DeleteIconButton
                onClick={e => {
                  e.stopPropagation();
                  void handleDeleteChannel(contactUuid, contactId, channel.id);
                }}
              />
            </div>
          )}
        </Group>
      </InfoRow.ValueCol>
    </InfoRow>
  );

  const renderPrimaryContacts = () => {
    if (!primaryContacts.length) return null;
    return (
      <Stack gap='md'>
        {primaryContacts.map(contact => {
          const channels = channelsByContactId[contact.id];
          const hasPhone = channels?.some(ch => ch.contact_type === 'phone') ?? false;
          const hasEmail = channels?.some(ch => ch.contact_type === 'email') ?? false;
          const birthdayStr = formatBirthday(contact.birthday as string | null | undefined);

          return (
            <Stack
              key={contact.id}
              gap='xs'
              className='contact-item'
              style={{ position: 'relative', cursor: 'pointer' }}
            >
              <Group gap='xs' align='flex-start' wrap='nowrap' justify='space-between'>
                <Group gap='xs' align='center' wrap='wrap' style={{ minWidth: 0, flex: 1 }}>
                  <EditableText
                    value={contact.name}
                    size='sm'
                    canEdit={canEditContact(contact)}
                    onSave={v => handleInlineSaveContactName(contact.id, v)}
                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                  />
                  {/* Бейджи-переключатели: без прав остаются метками, серые
                      hover-подсказки не показываем — включать всё равно нечего */}
                  {contact.is_decision_maker ? (
                    <Badge
                      size='xs'
                      color='green'
                      variant='light'
                      style={{ cursor: canEditContact(contact) ? 'pointer' : 'default' }}
                      onClick={
                        canEditContact(contact)
                          ? e => {
                              e.stopPropagation();
                              void handleToggleDecisionMaker(contact.id, true);
                            }
                          : undefined
                      }
                    >
                      ЛПР
                    </Badge>
                  ) : (
                    canEditContact(contact) && (
                      <Badge
                        size='xs'
                        color='gray'
                        variant='light'
                        className='lpr-hint'
                        style={{ opacity: 0, transition: 'opacity 0.2s ease', cursor: 'pointer' }}
                        onClick={e => {
                          e.stopPropagation();
                          void handleToggleDecisionMaker(contact.id, false);
                        }}
                      >
                        ЛПР
                      </Badge>
                    )
                  )}
                  {contact.is_primary ? (
                    <Badge
                      size='xs'
                      color='blue'
                      variant='light'
                      style={{ cursor: canEditContact(contact) ? 'pointer' : 'default' }}
                      onClick={
                        canEditContact(contact)
                          ? e => {
                              e.stopPropagation();
                              void handleTogglePrimary(contact.id, true);
                            }
                          : undefined
                      }
                    >
                      Основной
                    </Badge>
                  ) : (
                    canEditContact(contact) && (
                      <Badge
                        size='xs'
                        color='gray'
                        variant='light'
                        className='primary-hint'
                        style={{ opacity: 0, transition: 'opacity 0.2s ease', cursor: 'pointer' }}
                        onClick={e => {
                          e.stopPropagation();
                          void handleTogglePrimary(contact.id, false);
                        }}
                      >
                        Основной
                      </Badge>
                    )
                  )}
                  <Badge
                    size='xs'
                    color={contact.is_active === false ? 'gray' : 'teal'}
                    variant='light'
                    style={{ cursor: canEditContact(contact) ? 'pointer' : 'default' }}
                    onClick={
                      canEditContact(contact)
                        ? e => {
                            e.stopPropagation();
                            void handleToggleActive(contact.id);
                          }
                        : undefined
                    }
                  >
                    {contact.is_active === false ? 'Не активен' : 'Активен'}
                  </Badge>
                  {/* Без прав и без заполненного ДР показывать нечего: остаётся
                      только hover-подсказка, которая ничего не откроет */}
                  {(canEditContact(contact) || birthdayStr) && (
                    <Popover
                      opened={editingBirthdayForId === contact.id}
                      onClose={() => setEditingBirthdayForId(null)}
                      withArrow={false}
                      position='bottom-start'
                    >
                      <Popover.Target>
                        {birthdayStr ? (
                          <Group gap={2} wrap='nowrap'>
                            <Badge
                              size='xs'
                              color='yellow'
                              variant='light'
                              radius='sm'
                              style={{ cursor: canEditContact(contact) ? 'pointer' : 'default' }}
                              onClick={
                                canEditContact(contact)
                                  ? e => {
                                      e.stopPropagation();
                                      setEditingBirthdayForId(contact.id);
                                    }
                                  : undefined
                              }
                            >
                              ДР: {birthdayStr}
                            </Badge>
                            {canEditContact(contact) && (
                              <CloseButton
                                size='xs'
                                variant='subtle'
                                onClick={e => {
                                  e.stopPropagation();
                                  void handleInlineSaveBirthday(contact.id, null);
                                }}
                              />
                            )}
                          </Group>
                        ) : (
                          <Badge
                            size='xs'
                            color='gray'
                            variant='light'
                            radius='sm'
                            className='birthday-hint'
                            style={{
                              opacity: 0,
                              transition: 'opacity 0.2s ease',
                              cursor: 'pointer',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              setEditingBirthdayForId(contact.id);
                            }}
                          >
                            ДР
                          </Badge>
                        )}
                      </Popover.Target>
                      <Popover.Dropdown p='xs'>
                        <DatePicker
                          value={contact.birthday ? new Date(contact.birthday as string) : null}
                          onChange={date => {
                            void handleInlineSaveBirthday(contact.id, date);
                            setEditingBirthdayForId(null);
                          }}
                        />
                      </Popover.Dropdown>
                    </Popover>
                  )}
                </Group>
                {canEditContact(contact) && (
                  <Group
                    gap='xs'
                    align='flex-start'
                    style={{ opacity: 0, transition: 'opacity 0.2s ease' }}
                    className='delete-icon'
                  >
                    <DeleteIconButton
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteContact(contact.id);
                      }}
                    />
                  </Group>
                )}
              </Group>
              <EditableText
                value={contact.notes ?? ''}
                size='sm'
                canEdit={canEditContact(contact)}
                allowEmptySave
                placeholder='Заметки...'
                className={contact.notes ? undefined : 'entity-view-placeholder-dimmed'}
                onSave={v => handleInlineSaveContactNotes(contact.id, v)}
                style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              />
              <InfoRow>
                <InfoRow.TitleCol>Должность</InfoRow.TitleCol>
                <InfoRow.ValueCol>
                  <EditableText
                    value={contact.position ?? ''}
                    size='sm'
                    canEdit={canEditContact(contact)}
                    allowEmptySave
                    placeholder='Должность...'
                    className={contact.position ? undefined : 'entity-view-placeholder-dimmed'}
                    onSave={v => handleInlineSavePosition(contact.id, v)}
                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                  />
                </InfoRow.ValueCol>
              </InfoRow>
              {/* Все каналы связи */}
              {channels &&
                channels.map(channel => (
                  <Fragment key={channel.id}>
                    {renderContactValue(
                      channel,
                      contact.id,
                      contact.uuid,
                      CHANNEL_LABEL_MAP[channel.contact_type] ?? channel.contact_type
                    )}
                  </Fragment>
                ))}
              {/* Плейсхолдеры и быстрое добавление — affordance правки: без прав скрыты */}
              {canAdd && channels !== undefined && !hasPhone && (
                <InfoRow>
                  <InfoRow.TitleCol>Телефон</InfoRow.TitleCol>
                  <InfoRow.ValueCol>
                    <EditableText
                      value=''
                      size='sm'
                      canEdit
                      placeholder='Добавить...'
                      className='entity-view-placeholder-dimmed'
                      onSave={v => {
                        if (v.trim())
                          void handleInlineCreateContactChannel(
                            contact.uuid,
                            contact.id,
                            'phone',
                            v
                          );
                      }}
                      style={{ minWidth: VALUE_MIN_WIDTH, flexShrink: 0 }}
                    />
                  </InfoRow.ValueCol>
                </InfoRow>
              )}
              {/* Плейсхолдер для email если нет ни одного */}
              {canAdd && channels !== undefined && !hasEmail && (
                <InfoRow>
                  <InfoRow.TitleCol>Почта</InfoRow.TitleCol>
                  <InfoRow.ValueCol>
                    <EditableText
                      value=''
                      size='sm'
                      canEdit
                      placeholder='Добавить...'
                      className='entity-view-placeholder-dimmed'
                      onSave={v => {
                        if (v.trim())
                          void handleInlineCreateContactChannel(
                            contact.uuid,
                            contact.id,
                            'email',
                            v
                          );
                      }}
                      style={{ minWidth: VALUE_MIN_WIDTH, flexShrink: 0 }}
                    />
                  </InfoRow.ValueCol>
                </InfoRow>
              )}
              {/* Быстрое добавление канала для этого контакта: hover → выбор типа → inline-ввод */}
              {canAdd &&
                (quickAddingFor?.contactId === contact.id ? (
                  <div style={{ marginTop: 6 }}>
                    <InfoRow>
                      <InfoRow.TitleCol>
                        {QUICK_ADD_CONTACT_CHANNEL_TYPES.find(t => t.value === quickAddingFor.type)
                          ?.label ?? quickAddingFor.type}
                      </InfoRow.TitleCol>
                      <InfoRow.ValueCol>
                        <EditableText
                          value=''
                          size='sm'
                          canEdit
                          placeholder='Добавить...'
                          defaultEditing
                          onSave={v => {
                            const { contactId, contactUuid, type } = quickAddingFor;
                            setQuickAddingFor(null);
                            if (v.trim())
                              void handleInlineCreateContactChannel(
                                contactUuid,
                                contactId,
                                type,
                                v
                              );
                          }}
                          onCancel={() => setQuickAddingFor(null)}
                          style={{ minWidth: VALUE_MIN_WIDTH, flexShrink: 0 }}
                        />
                      </InfoRow.ValueCol>
                    </InfoRow>
                  </div>
                ) : (
                  <div style={{ marginTop: 6 }}>
                    <Menu
                      opened={quickAddMenuOpenForId === contact.id}
                      onClose={() => setQuickAddMenuOpenForId(null)}
                      position='bottom-start'
                      offset={4}
                      withinPortal
                    >
                      <Menu.Target>
                        <div
                          onClick={() => setQuickAddMenuOpenForId(contact.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <InfoRow>
                            <InfoRow.TitleCol>
                              <Text size='sm' c='dimmed' fs='italic'>
                                + Добавить
                              </Text>
                            </InfoRow.TitleCol>
                          </InfoRow>
                        </div>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {QUICK_ADD_CONTACT_CHANNEL_TYPES.map(({ value, label }) => (
                          <Menu.Item
                            key={value}
                            onClick={() =>
                              setQuickAddingFor({
                                contactId: contact.id,
                                contactUuid: contact.uuid,
                                type: value,
                              })
                            }
                          >
                            {label}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                ))}
              {primaryContacts.indexOf(contact) < primaryContacts.length - 1 && <Divider />}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  // Инициализация каналов из contact_channels (приходят с сервера через withContactChannelList)
  // Fallback на lazy-load для контактов без предзагруженных каналов
  useEffect(() => {
    let isCancelled = false;
    const needsLazyLoad: typeof contactPersons = [];

    for (const cp of contactPersons) {
      if (cp.contact_channels) {
        // Каналы уже загружены с сервера — используем напрямую
        setChannelsByContactId(prev => ({
          ...prev,
          [cp.id]: cp.contact_channels as ContactChannel[],
        }));
      } else if (!channelsByContactId[cp.id]) {
        needsLazyLoad.push(cp);
      }
    }

    // Fallback: если у каких-то контактов нет предзагруженных каналов
    if (needsLazyLoad.length > 0) {
      (async () => {
        for (const cp of needsLazyLoad) {
          try {
            const channels = await contactPersonRepository.getContactPersonContacts(
              cp.id as number
            );
            if (!isCancelled) {
              setChannelsByContactId(prev => ({ ...prev, [cp.id]: channels || [] }));
            }
          } catch (e) {
            logger.error(e);
          }
        }
      })();
    }

    return () => {
      isCancelled = true;
    };
  }, [contactPersons]);

  return (
    <>
      <style>{hoverStyles}</style>
      <ViewPanel>
        <Group justify='space-between' align='center' mb='xs'>
          <Text fw={400} style={{ fontSize: 14 }}>
            Контактные лица
          </Text>
          {canAdd && <AddIconButton onClick={() => setShowAddModal(true)} />}
        </Group>

        {showSearch && (
          <TextInput
            size='xs'
            placeholder='Поиск по имени, телефону, email...'
            value={searchValue}
            onChange={e => {
              const val = e.currentTarget.value;
              setSearchValue(val);
              if (!val.trim()) {
                setSearchResults(null);
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') void handleSearch(searchValue);
            }}
            rightSection={searchValue ? <CloseButton size='xs' onClick={clearSearch} /> : undefined}
            mb='xs'
          />
        )}

        {renderPrimaryContacts()}

        {primaryContacts.length > 0 && <Divider mt='xs' />}

        {/* Инлайн-добавление контактного лица по имени */}
        {canAdd && (
          <div style={{ marginTop: primaryContacts.length > 0 ? 12 : 0 }}>
            <EditableText
              value=''
              size='xs'
              canEdit
              placeholder='Добавить контактное лицо...'
              className='entity-view-placeholder-dimmed'
              onSave={v => {
                if (v.trim()) void handleInlineCreateContact(v);
              }}
            />
          </div>
        )}
      </ViewPanel>

      {/* Add Contact Modal */}
      <Modal
        opened={showAddModal}
        onClose={() => setShowAddModal(false)}
        title='Добавить контактное лицо'
        size='lg'
        zIndex={addModalZIndex}
      >
        <AddContactForm onSubmit={handleAddContact} onCancel={() => setShowAddModal(false)} />
      </Modal>

      {/* Edit Contact Modal */}
      <Modal
        opened={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingContact(null);
          setEditingIsPrimary(false);
        }}
        title='Редактировать контактное лицо'
        size='md'
        zIndex={editModalZIndex}
      >
        {editingContact ? (
          <EditContactForm
            contact={editingContact}
            isPrimary={editingIsPrimary}
            onChangeIsPrimary={setEditingIsPrimary}
            onSubmit={handleEditContact}
            onCancel={() => {
              setShowEditModal(false);
              setEditingContact(null);
              setEditingIsPrimary(false);
            }}
          />
        ) : (
          <Text c='red'>Ошибка: контакт не найден</Text>
        )}
      </Modal>

      {/* Add Contact Channel Modal */}
      <Modal
        opened={showAddChannelsModal}
        onClose={() => {
          setShowAddChannelsModal(false);
          setSelectedContactForChannels(null);
        }}
        title='Добавить контакт'
        size='md'
        zIndex={addChannelsModalZIndex}
      >
        <AddContactChannelForm
          onSubmit={handleAddContactChannels}
          onCancel={() => {
            setShowAddChannelsModal(false);
            setSelectedContactForChannels(null);
          }}
        />
      </Modal>

      {/* Edit Contact Channel Modal */}
      <Modal
        opened={showEditChannelModal}
        onClose={() => {
          setShowEditChannelModal(false);
          setEditingChannel(null);
        }}
        title='Редактировать контакт'
        size='md'
        zIndex={editChannelModalZIndex}
      >
        {editingChannel ? (
          <EditChannelForm
            channel={editingChannel}
            onSubmit={async (values: UpdateContactChannelData) => {
              if (!editingChannel) return;
              try {
                await contactChannelRepository.update(editingChannel.id, values);
                setShowEditChannelModal(false);
                setEditingChannel(null);
                // Invalidate cache for this contact's channels to trigger reload
                if (editingChannelContactId) {
                  const cid = editingChannelContactId;
                  setChannelsByContactId(prev => {
                    const next = { ...prev } as typeof prev;
                    delete next[cid];
                    return next;
                  });
                  // Immediately load fresh channels for the edited contact
                  try {
                    const fresh = await contactPersonRepository.getContactPersonContacts(cid);
                    setChannelsByContactId(prev => ({ ...prev, [cid]: fresh || [] }));
                  } catch (error) {
                    logger.error(error);
                  }
                  setEditingChannelContactId(null);
                } else {
                  onContactPersonsChanged();
                }
              } catch (error) {
                logger.error('Error updating channel:', error);
                throw error;
              }
            }}
            onCancel={() => {
              setShowEditChannelModal(false);
              setEditingChannel(null);
            }}
          />
        ) : (
          <Text c='red'>Ошибка: контакт не найден</Text>
        )}
      </Modal>
    </>
  );
}

// Add Contact Form Component
/**
 * Форма добавления контактного лица.
 * Дополнительно содержит чекбокс "Сделать основным для этого клиента" (is_primary).
 */
function AddContactForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (values: CreateContactPersonData, isPrimary: boolean) => void;
  onCancel: () => void;
}) {
  // Extend base create schema with optional contact fields (UI only)
  const extendedCreateSchema = React.useMemo(() => {
    const base = contactPersonSchemaDefinition.createSchema;
    return {
      ...base,
      properties: {
        ...base.properties,
        phone: StringField({
          title: 'Телефон',
          nullable: true,
          description: 'В формате 799933332211',
          maxLength: 50,
        }),
        phoneDescription: StringField({
          title: 'Описание телефона',
          description: 'Что за телефон',
          nullable: true,
          maxLength: 255,
        }),
        email: StringField({
          title: 'Email',
          nullable: true,
          description: 'Корректная почта abc@def.ru',
          maxLength: 255,
          format: 'email',
        }),
        emailDescription: StringField({
          title: 'Описание email',
          description: 'Доп. сведения',
          nullable: true,
          maxLength: 255,
        }),
      },
    };
  }, []);

  const SchemaForm = useSchemaForm(extendedCreateSchema, {
    onSubmitSuccess: () => {
      SchemaForm.setSuccess('Контактное лицо успешно добавлено!');
    },
  });

  const [isPrimary, setIsPrimary] = React.useState(false);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        return SchemaForm.handleSubmit((values: Record<string, unknown>) =>
          onSubmit(values as CreateContactPersonData, isPrimary)
        )(e);
      }}
    >
      <Stack gap='xs'>
        <SchemaForm.Messages />

        <SchemaForm.Field.name size='xs' />
        <Grid gutter='xs'>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <SchemaForm.Field.position size='xs' />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <SchemaForm.Field.birthday size='xs' />
          </Grid.Col>
        </Grid>
        <SchemaForm.Field.is_decision_maker size='xs' />
        <SchemaForm.Field.notes size='xs' />

        <Divider my='xs' label='Контактная информация' labelPosition='center' />

        <Grid gutter='xs'>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <SchemaForm.Field.phone size='xs' placeholder='+7 (999) 123-45-67' />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <SchemaForm.Field.phoneDescription
              size='xs'
              placeholder='Рабочий, личный, мобильный...'
            />
          </Grid.Col>
        </Grid>

        <Grid gutter='xs'>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <SchemaForm.Field.email size='xs' placeholder='example@company.com' />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <SchemaForm.Field.emailDescription size='xs' placeholder='Рабочий, личный...' />
          </Grid.Col>
        </Grid>

        <Divider my='xs' />
        <Checkbox
          size='xs'
          label='Сделать основным для этого клиента'
          checked={isPrimary}
          onChange={e => setIsPrimary(e.currentTarget.checked)}
        />

        <Group justify='flex-end' gap='xs'>
          <Button variant='light' onClick={onCancel} size='xs'>
            Отмена
          </Button>
          <SchemaForm.buttons.submit label='Добавить' />
        </Group>
      </Stack>
    </form>
  );
}

// Add Contact Channel Form Component
function AddContactChannelForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (values: AddContactChannelData) => void;
  onCancel: () => void;
}) {
  const SchemaForm = useSchemaForm(contactChannelSchemaDefinition.createSchema, {
    onSubmitSuccess: () => {
      SchemaForm.setSuccess('Контакт успешно добавлен!');
    },
  });

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        return SchemaForm.handleSubmit((values: Record<string, unknown>) =>
          onSubmit(values as AddContactChannelData)
        )(e);
      }}
    >
      <Stack gap='xs'>
        <SchemaForm.Messages />
        <Select
          label='Тип контакта'
          placeholder='Выберите тип контакта...'
          size='xs'
          data={[
            { value: 'phone', label: 'Телефон' },
            { value: 'email', label: 'Email' },
            { value: 'whatsapp', label: 'WhatsApp' },
            { value: 'telegram', label: 'Telegram' },
            { value: 'website', label: 'Сайт' },
          ]}
          {...SchemaForm.Field.contact_type.props}
        />

        <SchemaForm.Field.contact_value size='xs' />
        <SchemaForm.Field.description size='xs' />

        <Group justify='flex-end' gap='xs'>
          <Button variant='light' onClick={onCancel} size='xs'>
            Отмена
          </Button>
          <SchemaForm.buttons.submit label='Добавить' />
        </Group>
      </Stack>
    </form>
  );
}

// Edit Contact Form Component
function EditContactForm({
  contact,
  isPrimary,
  onChangeIsPrimary,
  onSubmit,
  onCancel,
}: {
  contact: ContactPerson;
  isPrimary: boolean;
  onChangeIsPrimary: (v: boolean) => void;
  onSubmit: (values: UpdateContactPersonData) => void;
  onCancel: () => void;
}) {
  // Guard against undefined contact
  if (!contact) {
    return <Text c='red'>Ошибка: контакт не найден</Text>;
  }

  const SchemaForm = useSchemaForm(contactPersonSchemaDefinition.updateSchema, {
    onSubmitSuccess: () => {
      SchemaForm.setSuccess('Контактное лицо успешно обновлено!');
    },
  });

  // Set initial data when contact is available
  React.useEffect(() => {
    if (contact) {
      logger.info('Setting form values with contact data:', contact);
      SchemaForm.setInitialData({
        name: contact.name,
        position: contact.position || '',
        birthday: contact.birthday || null,
        notes: contact.notes || '',
        is_decision_maker: contact.is_decision_maker ?? false,
      });
    }
  }, [contact]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        return SchemaForm.handleSubmit(onSubmit)(e);
      }}
    >
      <Stack gap='xs'>
        <SchemaForm.Messages />

        <SchemaForm.Field.name size='xs' />
        <SchemaForm.Field.position size='xs' />
        <SchemaForm.Field.birthday size='xs' />
        <SchemaForm.Field.is_decision_maker size='xs' />
        <SchemaForm.Field.notes size='xs' />

        <Divider my='xs' />
        <Checkbox
          size='xs'
          label='Сделать основным для этого клиента'
          checked={isPrimary}
          onChange={e => onChangeIsPrimary(e.currentTarget.checked)}
        />

        <Group justify='flex-end' gap='xs'>
          <Button variant='light' onClick={onCancel} size='xs'>
            Отмена
          </Button>
          <SchemaForm.buttons.submit label='Сохранить' />
        </Group>
      </Stack>
    </form>
  );
}
